import { Button, Checkbox, Input, Text } from "@ttc3k/trekker";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { RenewalRebaseGraceFragmentFragment } from "gql/generated";
import { formatRenewalDate } from "shared/utils";
import { Flex } from "styled-system/jsx";
import type { GraceChoice } from "./graceChoice";

const DATE_PATTERN = "MMMM d, yyyy";

type GracePeriodStepProps = {
  grace: RenewalRebaseGraceFragmentFragment;
  choice: GraceChoice;
  onChange: (choice: GraceChoice) => void;
  disabled?: boolean;
  membershipTranslationValues: Record<string, string>;
};

/**
 * Shown whenever a date change leaves members Overdue: explains the extended
 * renewal and sets how long they have before they expire.
 */
export const GracePeriodStep = ({
  grace,
  choice,
  onChange,
  disabled,
  membershipTranslationValues,
}: GracePeriodStepProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS.LAUNCH.GRACE",
  });
  const tv = {
    ...membershipTranslationValues,
    overdueCount: grace.overdueCount,
    targetDate: formatRenewalDate(grace.targetDate, DATE_PATTERN),
    nextRenewalDate: formatRenewalDate(grace.nextRenewalDate, DATE_PATTERN),
    currentGraceDays: grace.currentGraceDays,
    currentGraceEndsOn: formatRenewalDate(
      grace.currentGraceEndsOn,
      DATE_PATTERN,
    ),
    suggestedGraceDays: grace.suggestedGraceDays ?? 0,
  };

  // Pre-fill with the recommendation, never shortening a longer saved grace.
  useEffect(() => {
    if (choice.graceDays !== undefined || choice.neverExpire !== undefined) {
      return;
    }
    if (grace.currentNeverExpire) {
      onChange({ ...choice, neverExpire: true });
      return;
    }
    if (
      grace.suggestedGraceDays &&
      grace.suggestedGraceDays > grace.currentGraceDays
    ) {
      onChange({ ...choice, graceDays: grace.suggestedGraceDays });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-fill once per preview
  }, [grace.suggestedGraceDays, grace.currentGraceDays, grace.currentNeverExpire]);

  const neverExpire = choice.neverExpire ?? grace.currentNeverExpire;
  const days = choice.graceDays ?? grace.currentGraceDays;

  return (
    <Flex flexDir="column" gap="200">
      <Text visual="smallSemiBold" color="text.dark">
        {t("HEADING")}
      </Text>
      <Text size="sm" color="text.mid">
        {t("BODY", tv)}
      </Text>
      <Text size="sm" color="text.mid">
        {grace.currentNeverExpire
          ? t("CURRENT_NEVER", tv)
          : grace.currentGraceIsDefault
            ? t("CURRENT_DEFAULT", tv)
            : t("CURRENT", tv)}
      </Text>
      {grace.suggestedGraceDays != null && grace.suggestedGraceDays > 0 && (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap="200"
          flexWrap="wrap"
        >
          <Text size="sm" color="text.dark" flex="1" minW="0">
            {t("RECOMMEND", tv)}
          </Text>
          <Button
            visual="primary"
            mode="light"
            size="sm"
            type="button"
            disabled={
              disabled ||
              (!neverExpire && days === grace.suggestedGraceDays)
            }
            onClick={() =>
              onChange({
                ...choice,
                graceDays: grace.suggestedGraceDays ?? undefined,
                neverExpire: false,
                acknowledged: false,
              })
            }
          >
            {t("USE_RECOMMENDED", { days: grace.suggestedGraceDays })}
          </Button>
        </Flex>
      )}
      <Input
        label={t("DAYS_LABEL")}
        type="number"
        min={0}
        max={730}
        value={neverExpire ? "" : String(days)}
        disabled={disabled || neverExpire}
        onChange={e => {
          const raw = e.target.value;
          onChange({
            ...choice,
            graceDays:
              raw === ""
                ? undefined
                : Math.min(730, Math.max(0, Number(raw) || 0)),
            acknowledged: false,
          });
        }}
      />
      <Flex flexDir="column" gap="50">
        <Checkbox
          label={t("NEVER_EXPIRE_LABEL", tv)}
          checked={neverExpire}
          disabled={disabled}
          onCheckedChange={({ checked }: { checked: boolean | string }) =>
            onChange({
              ...choice,
              neverExpire: checked === true,
              acknowledged: false,
            })
          }
          alignItems="center"
          gap="200"
        />
        <Text size="sm" color="text.light">
          {t("NEVER_EXPIRE", tv)}
        </Text>
      </Flex>
      {grace.wouldExpireOnNextSweep > 0 && (
        <Flex flexDir="column" gap="100">
          <Text size="sm" color="text.error.mid">
            {t("WARNING", { ...tv, count: grace.wouldExpireOnNextSweep })}
          </Text>
          <Checkbox
            label={t("ACKNOWLEDGE", tv)}
            checked={choice.acknowledged}
            disabled={disabled}
            onCheckedChange={({ checked }: { checked: boolean | string }) =>
              onChange({ ...choice, acknowledged: checked === true })
            }
            alignItems="center"
            gap="200"
          />
        </Flex>
      )}
    </Flex>
  );
};

/** The one-line grace summary on review steps. */
export const GraceReviewLine = ({
  grace,
  membershipTranslationValues,
}: {
  grace: RenewalRebaseGraceFragmentFragment;
  membershipTranslationValues: Record<string, string>;
}) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS.LAUNCH.GRACE",
  });
  return (
    <Text size="sm" color="text.dark">
      {grace.neverExpire
        ? t("REVIEW_NEVER")
        : t("REVIEW_DAYS", {
            ...membershipTranslationValues,
            graceDays: grace.graceDays,
            graceEndsOn: formatRenewalDate(grace.graceEndsOn, DATE_PATTERN),
          })}
    </Text>
  );
};
